<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useSalesStore } from 'dashboard/stores/sales';
import SalesAPI from 'dashboard/api/sales';
import NonSalesAPI from 'dashboard/api/nonSales';
import SaleModal from 'dashboard/components-next/sales/SaleModal.vue';
import SaleCancelModal from 'dashboard/components-next/sales/SaleCancelModal.vue';
import NonSaleModal from 'dashboard/routes/dashboard/sales/components/NonSaleModal.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const salesStore = useSalesStore();
const saleModalRef = ref(null);
const cancelModalRef = ref(null);
const nonSaleModalRef = ref(null);

const sales = ref([]);
const nonSales = ref([]);
const loading = ref(false);
const pendingCancelSaleId = ref(null);

const brl = value =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value ?? 0);

const formatDate = iso => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const fetchSales = async () => {
  loading.value = true;
  try {
    const [salesRes, nonSalesRes] = await Promise.all([
      SalesAPI.getAllForConversation(props.conversationId),
      NonSalesAPI.getAllForConversation(props.conversationId),
    ]);
    sales.value = Array.isArray(salesRes.data) ? salesRes.data : [];
    nonSales.value = Array.isArray(nonSalesRes.data) ? nonSalesRes.data : [];
  } catch {
    sales.value = [];
    nonSales.value = [];
  } finally {
    loading.value = false;
  }
};

watch(() => props.conversationId, fetchSales, { immediate: true });

const openCreateModal = () => {
  saleModalRef.value?.openCreate(props.conversationId);
};

const openNonSaleModal = () => {
  nonSaleModalRef.value?.open(props.conversationId);
};

const handleNonSaleCreated = nonSale => {
  nonSales.value.unshift(nonSale);
};

const openEditModal = sale => {
  saleModalRef.value?.openEdit(props.conversationId, sale);
};

const openCancelModal = saleId => {
  pendingCancelSaleId.value = saleId;
  cancelModalRef.value?.open();
};

const handleSaleCreated = newSale => {
  sales.value.unshift(newSale);
};

const handleSaleUpdated = updatedSale => {
  const idx = sales.value.findIndex(s => s.id === updatedSale.id);
  if (idx !== -1) sales.value[idx] = updatedSale;
};

const handleCancelConfirmed = async reason => {
  const saleId = pendingCancelSaleId.value;
  if (!saleId) return;
  try {
    const data = await salesStore.cancelSaleForConversation(
      props.conversationId,
      saleId,
      reason
    );
    const idx = sales.value.findIndex(s => s.id === saleId);
    if (idx !== -1) sales.value[idx] = data;
    useAlert(t('SALES.CANCEL.SUCCESS'));
  } catch {
    useAlert(t('SALES.CANCEL.ERROR'), { variant: 'error' });
  } finally {
    pendingCancelSaleId.value = null;
  }
};
</script>

<template>
  <div class="px-1 py-2 flex flex-col gap-3">
    <!-- Loading -->
    <div v-if="loading" class="flex justify-center py-4">
      <span class="i-lucide-loader-circle animate-spin text-xl text-n-slate-9" />
    </div>

    <template v-else>
      <!-- Botões de ação -->
      <div class="flex gap-2">
        <Button
          :label="t('SALES.INFO_PANEL.REGISTER_SALE')"
          icon="i-lucide-badge-dollar-sign"
          size="sm"
          teal
          class="flex-1 justify-center"
          @click="openCreateModal"
        />
        <Button
          :label="t('SALES.NON_SALE.BUTTON')"
          icon="i-lucide-x-circle"
          size="sm"
          ghost
          slate
          class="flex-1 justify-center"
          @click="openNonSaleModal"
        />
      </div>

      <!-- Lista de vendas -->
      <div v-if="sales.length" class="flex flex-col gap-2">
        <div
          v-for="sale in sales"
          :key="sale.id"
          class="rounded-lg border p-3 flex flex-col gap-2"
          :class="sale.status === 'active' ? 'border-n-strong bg-n-alpha-3' : 'border-n-weak bg-n-alpha-1 opacity-70'"
        >
          <!-- Header da venda -->
          <div class="flex items-center justify-between">
            <span
              class="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full"
              :class="
                sale.status === 'active'
                  ? 'bg-s-green-100 text-s-green-700'
                  : 'bg-s-red-100 text-s-red-700'
              "
            >
              <span
                :class="
                  sale.status === 'active' ? 'i-lucide-check-circle' : 'i-lucide-x-circle'
                "
                class="text-[10px]"
              />
              {{
                sale.status === 'active'
                  ? t('SALES.INFO_PANEL.STATUS_ACTIVE')
                  : t('SALES.INFO_PANEL.STATUS_CANCELLED')
              }}
            </span>
            <span class="text-[11px] text-n-slate-9">{{ formatDate(sale.created_at) }}</span>
          </div>

          <!-- Dados -->
          <div class="grid grid-cols-2 gap-1.5 text-xs">
            <div>
              <p class="text-n-slate-9">{{ t('SALES.INFO_PANEL.ORDER_NUMBER') }}</p>
              <p
                class="font-medium text-n-slate-12"
                :class="{ 'line-through text-n-slate-10': sale.status === 'cancelled' }"
              >
                {{ sale.order_number }}
              </p>
            </div>
            <div>
              <p class="text-n-slate-9">{{ t('SALES.INFO_PANEL.AMOUNT') }}</p>
              <p
                class="font-medium"
                :class="
                  sale.status === 'active' ? 'text-s-green-600' : 'text-n-slate-10 line-through'
                "
              >
                {{ brl(sale.amount) }}
              </p>
            </div>
            <div class="col-span-2">
              <p class="text-n-slate-9">{{ t('SALES.INFO_PANEL.AGENT') }}</p>
              <p class="font-medium text-n-slate-12">{{ sale.agent_name }}</p>
            </div>
            <div v-if="sale.sale_type" class="col-span-2">
              <p class="text-n-slate-9">{{ t('SALES.SALE_TYPE.LABEL') }}</p>
              <p class="font-medium text-n-slate-12">{{ sale.sale_type }}</p>
            </div>
          </div>

          <!-- Motivo cancelamento -->
          <div
            v-if="sale.status === 'cancelled' && sale.cancellation_reason"
            class="rounded bg-n-alpha-2 px-2 py-1.5 text-xs text-n-slate-10"
          >
            <span class="font-medium text-n-slate-11">{{ t('SALES.INFO_PANEL.CANCELLATION_REASON') }}:</span>
            {{ sale.cancellation_reason }}
          </div>

          <!-- Ações (só para venda ativa) -->
          <div v-if="sale.status === 'active'" class="flex gap-2 mt-1">
            <Button
              :label="t('SALES.INFO_PANEL.EDIT')"
              icon="i-lucide-pencil"
              size="sm"
              ghost
              slate
              class="flex-1 justify-center"
              @click="openEditModal(sale)"
            />
            <Button
              :label="t('SALES.INFO_PANEL.CANCEL_SALE')"
              icon="i-lucide-x-circle"
              size="sm"
              ghost
              ruby
              class="flex-1 justify-center"
              @click="openCancelModal(sale.id)"
            />
          </div>
        </div>
      </div>

      <!-- Sem vendas -->
      <p v-if="!sales.length" class="text-xs text-n-slate-9 italic text-center">
        {{ t('SALES.INFO_PANEL.NO_SALE') }}
      </p>

      <!-- Não-vendas -->
      <div v-if="nonSales.length" class="flex flex-col gap-2 mt-2">
        <p class="text-xs font-semibold text-n-slate-11">
          {{ $t('SALES.NON_SALE.LIST_TITLE') }}
        </p>
        <div
          v-for="nonSale in nonSales"
          :key="nonSale.id"
          class="rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 flex flex-col gap-1"
        >
          <div class="flex items-center justify-between">
            <span class="text-[11px] font-medium text-n-slate-10">{{ nonSale.agent_name }}</span>
            <span class="text-[11px] text-n-slate-9">{{ formatDate(nonSale.created_at) }}</span>
          </div>
          <p class="text-xs text-n-slate-11">{{ nonSale.reason }}</p>
        </div>
      </div>
    </template>
  </div>

  <SaleModal
    ref="saleModalRef"
    @sale-created="handleSaleCreated"
    @sale-updated="handleSaleUpdated"
  />
  <SaleCancelModal ref="cancelModalRef" @confirmed="handleCancelConfirmed" />
  <NonSaleModal ref="nonSaleModalRef" @non-sale-created="handleNonSaleCreated" />
</template>
